import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const MONO = "'JetBrains Mono', 'Fira Code', Consolas, Monaco, monospace"
const SANS = "'Inter', system-ui, sans-serif"

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] },
})

function LogoLaxtro({ size = 36 }: { size?: number }) {
  const r = Math.round(size * 0.22)
  return (
    <div style={{
      width: size, height: size, borderRadius: r,
      background: 'var(--bg3)', border: '1px solid var(--border)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, userSelect: 'none',
    }}>
      <span style={{ fontFamily: SANS, fontSize: size * 0.48, fontWeight: 800, color: 'var(--amber)', lineHeight: 1 }}>L</span>
    </div>
  )
}

function LogoBLIPS({ size = 36 }: { size?: number }) {
  const r = Math.round(size * 0.22)
  return (
    <div style={{
      width: size, height: size, borderRadius: r,
      background: '#0f172a', border: '1px solid #1e3a5f',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, userSelect: 'none',
    }}>
      <span style={{ fontFamily: SANS, fontSize: size * 0.48, fontWeight: 800, color: '#60a5fa', lineHeight: 1 }}>B</span>
    </div>
  )
}

function LogoStealth({ size = 36 }: { size?: number }) {
  const r = Math.round(size * 0.22)
  return (
    <div style={{
      width: size, height: size, borderRadius: r,
      background: 'var(--bg3)', border: '1px solid var(--border)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, userSelect: 'none', fontSize: size * 0.52, lineHeight: 1,
    }}>
      👻
    </div>
  )
}

function getLogo(name: string, size = 36) {
  if (name === 'Laxtro') return <LogoLaxtro size={size} />
  if (name === 'BLIPS') return <LogoBLIPS size={size} />
  if (name === 'Stealth Agency') return <LogoStealth size={size} />
  return null
}

const SISTEMA_ART = `███████ ██ ███████ ████████ ███████ ███   ███    ██
██      ██ ██         ██    ██      ████ ████   ████
███████ ██ ███████    ██    █████   ██ ███ ██  ██  ██
     ██ ██      ██    ██    ██      ██  █  ██ ████████
███████ ██ ███████    ██    ███████ ██     ██ ██    ██`

const stats = [
  { v: 'R$300M+', l: 'receita gerada e influenciada' },
  { v: 'R$40k', l: 'MRR · agência de performance' },
  { v: '60k', l: 'leads gerados' },
  { v: '47', l: 'workflows n8n em produção' },
]

interface Project {
  name: string
  badge: string
  type: string
  status: string
  href: string | null
  desc: string
  metric: string | null
  stack: string[]
  previewDetails: string[]
}

const projects: Project[] = [
  {
    name: 'Laxtro',
    badge: 'Projeto solo',
    type: 'SaaS · Content OS · Multi-tenant',
    status: 'em construção',
    href: 'https://laxtro.com.br/',
    desc: 'Arquitetura, código e produto — tudo feito por uma pessoa. Pipeline para agências: DNA editorial da marca → pautas → roteiros → assets → distribuição por plataforma. Cada cliente isolado. Cron mensal.',
    metric: null,
    stack: ['React', 'TypeScript', 'Supabase', 'n8n', 'Claude API'],
    previewDetails: [
      'Multi-tenant desde dia 0: cada cliente em schema isolado no Supabase',
      'Pipeline: DNA editorial da marca → pautas → roteiros → assets HTML→PNG → captions por plataforma',
      'Cron mensal automático por cliente, executa sem intervenção manual',
      'Claude API como motor: copy estruturada baseada no DNA da marca cadastrado',
      'Decisão difícil: multi-tenant no início encarece, mas é a única forma de escalar sem reescrever depois',
    ],
  },
  {
    name: 'BLIPS',
    badge: 'Eng. de Crescimento',
    type: 'Fintech B2B',
    status: 'ativo',
    href: null,
    desc: 'Fintech de locação e financiamento de equipamentos B2B. Infraestrutura de dados (ETL, PostgreSQL), fallback de formulários, fleet WordPress hardened, GEO, campanhas de mídia paga.',
    metric: 'R$350M+/ano processados',
    stack: ['Python', 'FastAPI', 'PostgreSQL', 'WordPress', 'n8n'],
    previewDetails: [
      '60k leads gerados via funil de performance (Meta + Google Ads)',
      'ETL automático: Meta Ads API + Google Ads API → PostgreSQL + BigQuery',
      'Fleet de 9 servidores WordPress: SSH key-only, WAF mu-plugin, fail2ban, scanner de 16 camadas em cron',
      'GEO em 280 LPs: llms.txt, Content-Signal, schema — primeiro no setor B2B brasileiro',
      'PageSpeed mobile: 47 → 89 em 9 sites (+42pts em 2 semanas)',
      'Fallback de formulários: 4.200 leads recuperados · R$200k+ em receita preservada',
    ],
  },
  {
    name: 'Stealth Agency',
    badge: 'Growth Engineer',
    type: 'Agência de Performance · PME brasileira',
    status: 'R$40k MRR',
    href: null,
    desc: 'Tráfego pago, server-side GTM, automações de relatório e infraestrutura de conversão para PMEs. Meta Ads, Google Ads, sistemas de captação e rastreamento de ponta a ponta.',
    metric: '15 clientes ativos',
    stack: ['Meta Ads', 'Google Ads', 'sGTM', 'Docker', 'n8n'],
    previewDetails: [
      'Infraestrutura de tracking: sGTM, API de Conversões, hash SHA-256, deduplicação por event_id',
      'Automações de relatório: n8n + Claude API entregam análise de performance quinzenal',
      'Fleet de 30+ LPs com tracking, A/B testing e sGTM configurado em cada propriedade',
      'Pipeline de leads: captura → qualificação → distribuição com contexto no CRM',
      'R$0 → R$40k MRR em 18 meses, 2 pessoas, zero investidor externo',
    ],
  },
]

const cases = [
  {
    tag: 'BLIPS',
    prob: 'Formulários caindo em silêncio — sem erro visível, sem alerta. Leads perdidos sem rastro.',
    build: 'Sistema de fallback em 48h: validação em múltiplas camadas, fila de reenvio e reconciliação automática.',
    result: '4.200 leads recuperados · R$200k+ em receita que seria perdida',
  },
  {
    tag: 'GTM',
    prob: 'GA4 e Meta Pixel divergindo 40% nas conversões. Decisão de mídia baseada em dado errado.',
    build: 'Server-side GTM do zero: API de Conversões, hash SHA-256 de PII, deduplicação por event_id.',
    result: 'Match rate +85% · CPL reportado convergiu com o real',
  },
  {
    tag: 'GEO',
    prob: 'LPs invisíveis para ChatGPT, Claude e Perplexity em buscas comerciais B2B.',
    build: 'llms.txt estruturado, Content-Signal no robots.txt, schema markup em 280 páginas, entidade semântica GBP.',
    result: '280 LPs indexadas nas principais IAs · citações orgânicas sem pagar mídia',
  },
]

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontFamily: MONO,
      fontSize: '0.65rem',
      color: 'var(--muted)',
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
      marginBottom: '1.5rem',
    }}>{children}</div>
  )
}

function Divider() {
  return <div style={{ borderTop: '1px solid var(--border)', margin: '4rem 0' }} />
}

function PreviewModal({ project, onClose }: { project: Project; onClose: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handler)
      document.body.style.overflow = ''
    }
  }, [onClose])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(8,8,6,0.88)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1.5rem',
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg2)',
          border: '1px solid var(--border-strong)',
          borderRadius: 12,
          width: '100%',
          maxWidth: 580,
          maxHeight: '88vh',
          overflowY: 'auto',
        }}
      >
        {/* Terminal chrome */}
        <div style={{
          background: 'var(--bg3)',
          padding: '0 1.25rem',
          height: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--border)',
          borderRadius: '11px 11px 0 0',
          position: 'sticky', top: 0, zIndex: 1,
        }}>
          <span style={{ fontFamily: MONO, fontSize: '0.65rem', color: 'var(--muted)' }}>
            preview · {project.name.toLowerCase().replace(' ', '-')}
          </span>
          <button
            onClick={onClose}
            style={{ fontFamily: MONO, fontSize: '0.72rem', color: 'var(--muted)', padding: '0.2rem 0.4rem', borderRadius: 4, transition: 'color 0.15s, background 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.background = 'var(--border)' }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--muted)'; e.currentTarget.style.background = 'transparent' }}
          >esc ×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.75rem' }}>
          {/* Header: logo + name */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={{ flexShrink: 0, marginTop: 2 }}>
              {getLogo(project.name, 52)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                <span style={{ fontFamily: SANS, fontSize: '1.25rem', fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.025em' }}>
                  {project.name}
                </span>
                <span style={{
                  fontFamily: MONO, fontSize: '0.55rem',
                  color: project.status.includes('construção') ? 'var(--muted)' : 'var(--green)',
                  border: `1px solid ${project.status.includes('construção') ? 'var(--border)' : 'var(--green)'}`,
                  borderRadius: 3, padding: '0.1rem 0.4rem', flexShrink: 0,
                }}>{project.status}</span>
              </div>
              <div style={{ fontFamily: MONO, fontSize: '0.62rem', color: 'var(--muted)', marginBottom: project.metric ? '0.35rem' : 0 }}>
                <span style={{ color: 'var(--text)', fontWeight: 600 }}>{project.badge}</span>
                {' · '}{project.type}
              </div>
              {project.metric && (
                <div style={{ fontFamily: MONO, fontSize: '0.82rem', fontWeight: 700, color: 'var(--amber)' }}>
                  {project.metric}
                </div>
              )}
            </div>
          </div>

          {/* Stealth disclaimer */}
          {project.name === 'Stealth Agency' && (
            <div style={{
              fontFamily: MONO, fontSize: '0.68rem', color: 'var(--muted)',
              border: '1px solid var(--border)', borderRadius: 6,
              padding: '0.65rem 1rem', marginBottom: '1.25rem', lineHeight: 1.65,
            }}>
              identidade não divulgada por design. o trabalho fala por si.
            </div>
          )}

          {/* Description */}
          <p style={{ fontSize: '0.88rem', color: 'var(--muted)', lineHeight: 1.7, margin: '0 0 1.5rem' }}>
            {project.desc}
          </p>

          {/* What was built */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ fontFamily: MONO, fontSize: '0.58rem', color: 'var(--muted)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
              o que foi construído
            </div>
            {project.previewDetails.map((d, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.65rem', marginBottom: '0.55rem', alignItems: 'flex-start' }}>
                <span style={{ fontFamily: MONO, fontSize: '0.7rem', color: 'var(--green)', flexShrink: 0, marginTop: '0.18rem' }}>→</span>
                <span style={{ fontSize: '0.83rem', color: 'var(--muted)', lineHeight: 1.65 }}>{d}</span>
              </div>
            ))}
          </div>

          {/* Stack */}
          <div style={{ marginBottom: project.href ? '1.5rem' : 0 }}>
            <div style={{ fontFamily: MONO, fontSize: '0.58rem', color: 'var(--muted)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '0.6rem' }}>
              stack
            </div>
            <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
              {project.stack.map(t => (
                <span key={t} style={{
                  fontFamily: MONO, fontSize: '0.62rem',
                  color: 'var(--muted)', border: '1px solid var(--border)',
                  borderRadius: 4, padding: '0.18rem 0.5rem',
                }}>{t}</span>
              ))}
            </div>
          </div>

          {/* External link */}
          {project.href && (
            <a
              href={project.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center',
                fontFamily: MONO, fontSize: '0.75rem', color: 'var(--text)',
                border: '1px solid var(--border-strong)', borderRadius: 6,
                padding: '0.5rem 1rem', transition: 'border-color 0.15s, background 0.15s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--muted)'; (e.currentTarget as HTMLAnchorElement).style.background = 'var(--bg3)' }}
              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--border-strong)'; (e.currentTarget as HTMLAnchorElement).style.background = 'transparent' }}
            >
              laxtro.com.br ↗
            </a>
          )}
        </div>
      </motion.div>
    </motion.div>
  )
}

export default function Hero() {
  const [openProject, setOpenProject] = useState<string | null>(null)
  const activeProject = projects.find(p => p.name === openProject) ?? null

  return (
    <>
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '5rem 1.5rem 3rem' }}>
        {/* ── TERMINAL ── */}
        <motion.div {...fade(0)} style={{ marginBottom: '3.5rem' }}>
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            {/* chrome */}
            <div style={{
              background: 'var(--bg3)',
              padding: '0 1rem',
              height: 36,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              borderBottom: '1px solid var(--border)',
            }}>
              <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#ff5f56', display: 'inline-block', flexShrink: 0 }} />
              <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#febc2e', display: 'inline-block', flexShrink: 0 }} />
              <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#27c840', display: 'inline-block', flexShrink: 0 }} />
              <span style={{ marginLeft: 'auto', fontFamily: MONO, fontSize: '0.68rem', color: 'var(--muted)', userSelect: 'none' }}>
                matheus@aios — sistema
              </span>
            </div>
            {/* body */}
            <div style={{ padding: '1.5rem 1.75rem 1.75rem', background: 'var(--bg2)', overflowX: 'auto' }}>
              <pre style={{
                fontFamily: MONO,
                fontSize: 'clamp(0.45rem, 1.05vw, 0.62rem)',
                lineHeight: 1.55,
                color: 'var(--green)',
                margin: 0,
                whiteSpace: 'pre',
              }}>{SISTEMA_ART}</pre>

              <div style={{ fontFamily: MONO, fontSize: '0.82rem', marginTop: '1.5rem' }}>
                <div style={{ color: 'var(--muted)', marginBottom: '0.6rem' }}>
                  <span style={{ color: 'var(--muted2)' }}>$ </span>whoami
                </div>
                <h1 style={{ fontWeight: 700, fontSize: 'clamp(1.15rem, 2.4vw, 1.4rem)', letterSpacing: '-0.02em', lineHeight: 1.2, color: 'var(--text)', margin: '0 0 0.35rem', fontFamily: SANS }}>
                  Matheus Edson Moreira Linhares
                </h1>
                <p style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--text)', margin: '0 0 0.6rem', fontFamily: SANS }}>
                  Growth Engineer. 21 anos. Uberlândia.
                </p>
                <p style={{ color: 'var(--muted)', margin: '0 0 0.4rem', maxWidth: 660, lineHeight: 1.7 }}>
                  Agência de performance: R$40k MRR, 15 clientes ativos.
                  Full stack de verdade: da campanha no Meta ao servidor que sustenta tudo.
                </p>
                <p style={{ color: 'var(--muted)', margin: '0 0 0.4rem', maxWidth: 660, lineHeight: 1.7 }}>
                  Growth Engineer em uma fintech de R$350M/ano. Infra, segurança e dados.
                </p>
                <p style={{ color: 'var(--muted)', margin: '0 0 1.25rem', maxWidth: 660, lineHeight: 1.7 }}>
                  Engenharia da Computação, UFU 4° período. Comecei a trabalhar antes de terminar o curso.
                  O mercado ensinou mais rápido que qualquer grade curricular.
                </p>
                <div style={{ color: 'var(--muted)' }}>
                  <span style={{ color: 'var(--muted2)' }}>$ </span>
                  <span style={{ display: 'inline-block', width: 8, height: '1em', background: 'var(--muted)', verticalAlign: 'text-bottom', animation: 'blink 1.2s step-end infinite' }} />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ── ESCALA ── stat bar */}
        <motion.div {...fade(0.06)} style={{ marginBottom: '4rem' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '0',
            border: '1px solid var(--border)',
            borderRadius: 8,
            overflow: 'hidden',
          }}>
            {stats.map((s, i) => (
              <div key={s.v} style={{
                padding: '1.5rem 1.75rem',
                borderRight: i < stats.length - 1 ? '1px solid var(--border)' : undefined,
              }}>
                <div style={{
                  fontFamily: SANS,
                  fontSize: 'clamp(1.5rem, 2.8vw, 2.2rem)',
                  fontWeight: 800,
                  color: 'var(--amber)',
                  letterSpacing: '-0.03em',
                  lineHeight: 1,
                  marginBottom: '0.5rem',
                }}>{s.v}</div>
                <div style={{
                  fontFamily: MONO,
                  fontSize: '0.65rem',
                  color: 'var(--muted)',
                  lineHeight: 1.4,
                }}>{s.l}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* ── PROJETOS ── */}
        <motion.div {...fade(0.1)}>
          <Label>projetos</Label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))',
            gap: '1rem',
          }}>
            {projects.map(p => (
              <div
                key={p.name}
                onClick={() => setOpenProject(p.name)}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s',
                }}
                onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border-strong)'}
                onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)'}
              >
                <div style={{
                  padding: '1.25rem 1.5rem 1rem',
                  borderBottom: '1px solid var(--border)',
                  background: 'var(--bg2)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.1rem' }}>
                    {getLogo(p.name)}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.2rem' }}>
                        <span style={{ fontFamily: SANS, fontSize: '1.05rem', fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.015em' }}>
                          {p.name}
                        </span>
                        <span style={{
                          fontFamily: MONO, fontSize: '0.58rem',
                          color: p.status.includes('construção') ? 'var(--muted)' : 'var(--green)',
                          border: `1px solid ${p.status.includes('construção') ? 'var(--border)' : 'var(--green)'}`,
                          borderRadius: 3, padding: '0.12rem 0.4rem', flexShrink: 0,
                        }}>{p.status}</span>
                      </div>
                      <div style={{ fontFamily: MONO, fontSize: '0.62rem', color: 'var(--muted)', letterSpacing: '0.04em' }}>
                        <span style={{ color: 'var(--text)', fontWeight: 600 }}>{p.badge}</span>
                        {' · '}{p.type}
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ padding: '1.25rem 1.5rem', flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <p style={{ fontSize: '0.85rem', color: 'var(--muted)', lineHeight: 1.65, margin: 0, flex: 1 }}>{p.desc}</p>

                  {p.metric && (
                    <div style={{ fontFamily: MONO, fontSize: '0.82rem', fontWeight: 700, color: 'var(--text)' }}>
                      {p.metric}
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                      {p.stack.map(t => (
                        <span key={t} style={{
                          fontFamily: MONO, fontSize: '0.58rem',
                          color: 'var(--muted)', border: '1px solid var(--border)',
                          borderRadius: 3, padding: '0.12rem 0.4rem',
                        }}>{t}</span>
                      ))}
                    </div>
                    {p.href && (
                      <a
                        href={p.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{ fontFamily: MONO, fontSize: '0.65rem', color: 'var(--muted)', transition: 'color 0.15s', flexShrink: 0 }}
                        onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.color = 'var(--text)'}
                        onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.color = 'var(--muted)'}
                      >laxtro.com.br ↗</a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <Divider />

        {/* ── CASOS ── */}
        <motion.div {...fade(0.14)}>
          <Label>casos</Label>
          <div>
            {cases.map((c, i) => (
              <div key={c.tag} style={{
                display: 'grid',
                gridTemplateColumns: '60px 1fr',
                gap: '1.5rem',
                padding: '1.5rem 0',
                borderTop: '1px solid var(--border)',
                borderBottom: i === cases.length - 1 ? '1px solid var(--border)' : undefined,
              }}>
                <div style={{ fontFamily: MONO, fontSize: '0.62rem', fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.1em', paddingTop: '0.2rem' }}>
                  {c.tag}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  <p style={{ fontSize: '0.88rem', color: 'var(--muted)', lineHeight: 1.6, margin: 0 }}>{c.prob}</p>
                  <p style={{ fontFamily: MONO, fontSize: '0.76rem', color: 'var(--muted)', lineHeight: 1.6, margin: 0, borderLeft: '2px solid var(--border)', paddingLeft: '0.75rem' }}>{c.build}</p>
                  <div style={{ fontFamily: MONO, fontSize: '0.82rem', fontWeight: 700, color: 'var(--text)' }}>{c.result}</div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <Divider />

        {/* ── EM PRODUÇÃO ── */}
        <motion.div {...fade(0.18)}>
          <Label>em produção</Label>
          <div style={{ fontFamily: MONO, fontSize: '0.82rem', lineHeight: 1.95 }}>
            {[
              { tag: 'AIOS', desc: '20+ agentes Claude Code em produção, least-privilege por papel, KB + memória persistente cross-session' },
              { tag: 'N8N', desc: '47 workflows em queue mode (1 main + 2 workers + Redis) — zero downtime há 6 meses' },
              { tag: 'WP', desc: 'Fleet de 9 servidores: SSH key-only, WAF mu-plugin, scanner automatizado de 16 camadas em cron' },
              { tag: 'sGTM', desc: '3 propriedades com server-side GTM — CAPI, deduplicação, hash de PII — match rate +85%' },
              { tag: 'GEO', desc: '280 LPs indexadas em ChatGPT/Claude/Perplexity via llms.txt + Content-Signal + schema markup' },
            ].map(c => (
              <div key={c.tag} style={{ display: 'flex', gap: '1.5rem', alignItems: 'baseline' }}>
                <span style={{ color: 'var(--muted)', fontWeight: 700, fontSize: '0.62rem', letterSpacing: '0.1em', width: 44, textAlign: 'right', flexShrink: 0 }}>{c.tag}</span>
                <span style={{ color: 'var(--muted)' }}>{c.desc}</span>
              </div>
            ))}
          </div>
        </motion.div>

      </section>

      {/* ── PREVIEW MODAL ── */}
      <AnimatePresence>
        {activeProject && (
          <PreviewModal project={activeProject} onClose={() => setOpenProject(null)} />
        )}
      </AnimatePresence>
    </>
  )
}
